import React, { useMemo, useCallback } from "react";
import { TaxIdentity as TaxIdentityApi } from '../../api/services/next';
import GridSearchTemplate from "../GridSearchTemplate";
import Checkbox from '../../components/shared/controlled/Checkbox';
import CheckboxToggle from '../../components/shared/CheckboxToggle';
import { useTextResources } from '../../language';
import IsActiveColumn from './grid/IsActiveColumn';
import ActionsColumn from './grid/ActionsColumn';
import UserColumn from './grid/UserColumn';
import AddTaxIdentityPopup from "./popups/AddTaxIdentityPopup";
import GridMenu from "./components/GridMenu";
import { useLoader } from '../../components/shared/utils';
import { onError, onSuccess } from '../../components/shared/messages';
import useSafeState from '../../hooks/useSafeState';
import { dateFormat } from '../../components/shared/constants';
import moment from "moment";

const defaultSearchSpec = {
    IsActive: true,
};

const IsraelTaxAuthority = () => {
    const text = useTextResources();
    const loader = useLoader();

    const [spec, setSpec] = useSafeState(defaultSearchSpec);
    const [data, setData] = useSafeState([]);

    const refreshData = useCallback(async () => {
        const newData = await TaxIdentityApi.GetTaxIdentities(spec);
        setData(newData);
    }, [spec, setData]);

    const refreshDataWithLoader = useCallback(async () => {
        try {
            loader(true);
            await refreshData();
        } catch(error) {
            onError(error);
        } finally {
            loader(false);
        }
    }, [loader, refreshData]);

    const handleSearchChange = useCallback((spec) => {
        setSpec(spec);
    }, [setSpec]);

    const handleSearchComplete = useCallback((data) => {
        setData(data);
    }, [setData]);

    const dateFormatter = ({ value }) => {
        return value && moment(value).format(dateFormat) || '';
      };

    const saveTaxIdentity = useCallback(async (taxIdentityToSave) => {
        try {
            loader(true);
            await TaxIdentityApi.CreateOrUpdateTaxIdentity(taxIdentityToSave);
            await refreshData();
            onSuccess();
        } catch (error) {
            onError(error);
            throw error;
        } finally {
            loader(false);
        }
    }, [loader, refreshData]);

    const gridOptions = useMemo(() => ({
        search: {
            fields: [
                <Checkbox
                    label={text('ShowOnlyActive')}
                    key='IsActive'
                />,
            ],
            api: TaxIdentityApi.GetTaxIdentities,
            method: 'GET',
            searchOnLoad: true,
            initial: defaultSearchSpec,
        },
        grid: {
            columns: [
                { name: 'Name', title: text('Name') },
                {
                    name: 'User',
                    title: text('User'),
                    formatter: UserColumn,
                },        
                { name: 'ClientId', title: text('ClientId') },
                {
                    name: 'TokenCreateDate',
                    title: text('TokenCreateDate'),
                    formatter: dateFormatter,
                },
                {
                    name: 'TokenExpirationDate',
                    title: text('TokenExpirationDate'),
                    formatter: dateFormatter
                },
                {
                    name: 'IsActive',
                    title: text('Active'),
                    formatter: IsActiveColumn,
                    customFilter: CheckboxToggle,
                },
                {
                    name: 'Actions',
                    title: text('Actions'),
                    formatter: ActionsColumn,
                    saveTaxIdentity,
                },
            ],
            paging: true,
            filtering: {
                extensions: [
                    { columnName: 'User', filteringEnabled: false },
                    { columnName: 'Actions', filteringEnabled: false },
                ],
                defaults: [
                    { columnName: 'IsActive', value: true },
                ],
            },
            menu: [
                <GridMenu
                    key="gridMenu"
                    taxIdentities={data}
                    refreshData={refreshDataWithLoader}
                />
            ],
            customOptions: {
                sort: {
                    sorting: [
                        { columnName: 'Name', direction: 'asc' },
                    ],
                },
            },
            editable: {
                allowAdd: true,
                allowEdit: false,
                modalEditor: {
                    modal: AddTaxIdentityPopup,
                    props: {
                        saveTaxIdentity,
                    }
                },
            },
        },
    }), [data, refreshDataWithLoader, saveTaxIdentity, text]);

    return (
        <GridSearchTemplate
            options={gridOptions}
            onSearchChange={handleSearchChange}
            onSearchComplete={handleSearchComplete}
            data={data}
        />
    )
};

export default IsraelTaxAuthority;
